import { useMemo } from 'react';
import { AerosyncEnvironment, WidgetConfigs } from 'aerosync-web-sdk';

import PublicEnv from 'shared/utils/public-env';

import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';

import { useStartAeroSyncConnection } from './use-start-aerosync-connection';
import { useAerosyncEventHandlers } from './use-aerosync-event-handlers';

export type UseAerosyncResponse = {
  aeroSyncWidgetOptions: WidgetConfigs;
  isReady: boolean;
  AEROSYNC_WIDGET_ID: string;
};

const AEROSYNC_WIDGET_ID = 'aerosync-widget-id';

export const AEROSYNC_WIDGET_STYLE: WidgetConfigs['style'] = {
  width: '375px',
  height: '688px',
  bgColor: '#000000',
  opacity: 0.7,
};

const isProduction = PublicEnv.appEnv === 'production';

export const AEROSYNC_CONFIGURATION_ID = PublicEnv.aerosyncConfigurationId;

export const AEROSYNC_ENVIRONMENT = isProduction ? AerosyncEnvironment.Production : AerosyncEnvironment.Sandbox;

export const useAerosync = (): UseAerosyncResponse => {
  // Hooks
  const {
    whiteLabelConfig: { brandedDutchiePayName = '' },
  } = useDutchiePay();
  const { token, configurationId, aeroPassUserUuid, loading, error } = useStartAeroSyncConnection({
    brandedDutchiePayName,
  });
  const { onClose, onSuccess, onError, onEvent, onLoad } = useAerosyncEventHandlers({
    brandedDutchiePayName,
  });
  // Derived state
  const isReady = !loading && !error && !!token && !!configurationId && !!aeroPassUserUuid;

  return useMemo(
    () => ({
      aeroSyncWidgetOptions: {
        elementId: AEROSYNC_WIDGET_ID,
        iframeTitle: 'Connect Bank Account',
        environment: AEROSYNC_ENVIRONMENT,
        token,
        style: AEROSYNC_WIDGET_STYLE,
        handleMFA: false,
        jobId: '',
        connectionId: '',
        configurationId: AEROSYNC_CONFIGURATION_ID,
        aeroPassUserUuid,
        onEvent,
        onSuccess,
        onClose,
        onError,
        onLoad,
      },
      isReady,
      AEROSYNC_WIDGET_ID,
    }),
    [token, aeroPassUserUuid, onEvent, onSuccess, onClose, onError, onLoad, isReady]
  );
};
